/**
 * The budget panel — "am I on track?", answered beside the journal.
 *
 * Each bucket is a ratio against a limit, which is a METER, not a chart: a
 * same-ramp track whose fill carries severity, with the numbers stated in text
 * beside it. The bar is decorative — every value it encodes is written out — so
 * it is hidden from assistive tech rather than narrated twice.
 *
 * The fill deliberately does NOT use the bucket's own colour. Needs teal, Wants
 * amber and Savings green are IDENTITY, carried here by the dot and the name;
 * reusing them for the bar would make length and identity fight over the same
 * channel, and a green "Savings" bar would read as "good" rather than as
 * "savings". The fill is its own status ramp, checked against all three bucket
 * hues so it cannot be mistaken for one of them.
 *
 * TWO TIME SCALES SHARE THIS PANEL, and the difference matters.
 *
 * The METER is one month: spent against what this month's income allotted. It
 * is the discipline question — "did March behave".
 *
 * The BALANCE beside it is cumulative: last month's leftover plus this month's
 * share minus what went out, carried forward forever (AGENTS.md §3.7). It is
 * the money question — "what have I got".
 *
 * They can disagree, and that is information rather than a bug: a full bar
 * with a healthy balance means you overspent a month you could afford to. The
 * bar is never redrawn from the balance, because a meter whose denominator
 * grows every month measures nothing.
 */
import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type { BudgetProfileInput, PeriodSummary } from '@et/contracts';
import { BUCKET_LABELS, type SpendingBucket } from '@et/core';
import { api, messageOf, type Identity } from './api.js';
import { BucketGuide } from './BucketGuide.js';
import { monthLabel } from './months.js';

interface Props {
  identity: Identity;
  summary: PeriodSummary | null;
  month: string;
  canEditProfile: boolean;
  onProfileSaved: () => void;
  loading: boolean;
}

function pct(bps: number | null): string {
  return bps === null ? '—' : `${Math.round(bps / 100)}%`;
}

/** Amounts arrive as decimal strings; '-' is the only sign they carry. */
const isNegative = (amount: string): boolean => amount.startsWith('-');
const isZero = (amount: string): boolean => Number(amount) === 0;
const unsigned = (amount: string): string => amount.replace('-', '');

type MeterState = 'ok' | 'close' | 'over' | 'unset';

/**
 * Severity from how much of the bucket is gone. "close" earns its own step
 * because the useful moment is before the line is crossed, not after.
 */
function meterState(consumedBps: number | null): MeterState {
  if (consumedBps === null) return 'unset';
  if (consumedBps > 10_000) return 'over';
  if (consumedBps >= 9_000) return 'close';
  return 'ok';
}

/**
 * The panel when it is closed: a narrow rail on the edge of the journal.
 *
 * Closing it entirely would hide the fact that it exists, and a toggle up in
 * the header is a long way from the thing it toggles. So the collapsed state
 * keeps its place in the layout and keeps saying something — three bars and
 * three percentages, which is enough to notice that Wants is nearly gone
 * without opening anything.
 */
export function BudgetRail({
  summary,
  onOpen,
}: {
  summary: PeriodSummary | null;
  onOpen: () => void;
}) {
  return (
    <aside className="budget-rail">
      <button type="button" className="rail-open" onClick={onOpen} aria-label="Show budget">
        <span aria-hidden="true">‹</span>
      </button>

      <span className="rail-title" aria-hidden="true">
        Budget
      </span>

      {summary && (
        <ul className="rail-meters">
          {summary.buckets.map((b) => {
            const state = meterState(b.consumedBps);
            const filled = Math.min(100, Math.max(0, (b.consumedBps ?? 0) / 100));

            return (
              <li key={b.bucket}>
                {/* The only label that fits is the dot, so the accessible name
                    carries what the eye gets from position and colour. */}
                <span
                  className={`dot dot-${b.bucket.toLowerCase()}`}
                  role="img"
                  aria-label={`${BUCKET_LABELS[b.bucket as SpendingBucket]}: ${b.actual} of ${b.planned}`}
                />
                <span className="rail-bar" data-state={state} aria-hidden="true">
                  <span style={{ width: `${filled}%` }} />
                </span>
                <span className="rail-pct">{pct(b.consumedBps)}</span>
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
}

export function Budget({
  identity,
  summary,
  month,
  canEditProfile,
  onProfileSaved,
  loading,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  if (!summary) {
    return (
      <section className="budget">
        <p className="hint">{loading ? 'Loading…' : 'No budget data for this month.'}</p>
      </section>
    );
  }

  const currency = summary.currency;
  const overspent = summary.unallocated.startsWith('-');

  return (
    <section className="budget">
      <div className="budget-pool">
        <p className="section-total">
          Available{' '}
          <strong className={isNegative(summary.pool) ? 'over' : undefined}>
            {summary.pool} {currency}
          </strong>
        </p>
        <p className="hint">
          Everything earned minus everything spent, all time — {summary.totalIncome} in,{' '}
          {summary.totalSpent} out. Income does not expire at the end of the month.
        </p>
      </div>

      <p className="section-total section-total-sub">
        Income this month{' '}
        <strong>
          {summary.netIncome} {currency}
        </strong>
      </p>

      <ul className="meters">
        {summary.buckets.map((b) => {
          const state = meterState(b.consumedBps);
          // Clamped: past the limit the bar is full and the overspend is said
          // in words. A bar drawn past its own track is not a bar.
          const filled = Math.min(100, Math.max(0, (b.consumedBps ?? 0) / 100));

          return (
            <li className="meter" key={b.bucket}>
              <div className="meter-head">
                <span className="meter-name">
                  <span className={`dot dot-${b.bucket.toLowerCase()}`} aria-hidden="true" />
                  {BUCKET_LABELS[b.bucket as SpendingBucket]}
                  <em>{b.plannedPct}%</em>
                </span>
                <span className="meter-spent">{b.actual}</span>
              </div>

              <div className="meter-track" data-state={state} aria-hidden="true">
                <span className="meter-fill" style={{ width: `${filled}%` }} />
              </div>

              <div className="meter-foot">
                <span>
                  of {b.planned} {currency}
                  {!isZero(b.carriedIn) && (
                    <em className="meter-carried">
                      {' '}
                      {isNegative(b.carriedIn) ? '−' : '+'}
                      {unsigned(b.carriedIn)} carried
                    </em>
                  )}
                </span>
                {/* The BALANCE, not this month's remainder. With rollover the
                    two differ, and the balance is what you actually spend
                    from — so it is the one that gets the space. */}
                <span className={isNegative(b.available) ? 'meter-over' : undefined}>
                  {isNegative(b.available)
                    ? `${unsigned(b.available)} short`
                    : `${b.available} left`}
                </span>
              </div>
            </li>
          );
        })}
      </ul>

      <p className={overspent ? 'verdict verdict-over' : 'verdict verdict-ok'}>
        {overspent ? (
          <>
            Spent <strong>{summary.unallocated.replace('-', '')} {currency}</strong> more than came
            in this month.
          </>
        ) : (
          <>
            <strong>
              {summary.unallocated} {currency}
            </strong>{' '}
            of this month's income is still unspent — and stays available next month.
          </>
        )}
      </p>

      {/* The spreadsheet's own columns, kept for the times the meters are not
          enough — folded away because they answer a different question. */}
      <details className="budget-detail">
        <summary>Full breakdown</summary>
        <div className="tablewrap">
          <table className="budget-table">
            <thead>
              <tr>
                <th>Bucket</th>
                <th className="num">Carried in</th>
                <th className="num">Should be</th>
                <th className="num">Goal</th>
                <th className="num">Actual</th>
                <th className="num">Spent</th>
                <th className="num">Available</th>
              </tr>
            </thead>
            <tbody>
              {summary.buckets.map((b) => (
                <tr key={b.bucket}>
                  <th scope="row">
                    <span className={`dot dot-${b.bucket.toLowerCase()}`} aria-hidden="true" />
                    {BUCKET_LABELS[b.bucket as SpendingBucket]}
                  </th>
                  <td className={`num ${isNegative(b.carriedIn) ? 'over' : 'muted'}`}>
                    {isZero(b.carriedIn) ? '—' : b.carriedIn}
                  </td>
                  <td className="num">{b.planned}</td>
                  <td className="num muted">{b.plannedPct}%</td>
                  <td className={`num ${b.status === 'over' ? 'over' : ''}`}>
                    {pct(b.actualShareBps)}
                  </td>
                  <td className="num">{b.actual}</td>
                  {/* The balance, carry included — not `remaining`, which only
                      ever describes this month and is floored at zero. */}
                  <td className={`num ${isNegative(b.available) ? 'over' : 'under'}`}>
                    {b.available}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">Total</th>
                <td className="num" />
                <td className="num">{summary.totalPlanned}</td>
                <td className="num muted">100%</td>
                <td className="num" />
                <td className="num strong">{summary.totalActual}</td>
                {/* Equals the three Available cells above, by construction. */}
                <td className={`num strong ${isNegative(summary.pool) ? 'over' : ''}`}>
                  {summary.pool}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </details>

      <div className="budget-actions">
        <button type="button" className="chip chip-ghost" onClick={() => setGuideOpen(true)}>
          What goes where?
        </button>
        {canEditProfile && !editing && (
          <button type="button" className="chip chip-ghost" onClick={() => setEditing(true)}>
            Change the split ({summary.profile.needsPct}/{summary.profile.wantsPct}/
            {summary.profile.savingsPct})
          </button>
        )}
      </div>

      {guideOpen && (
        <BucketGuide
          needsPct={summary.profile.needsPct}
          wantsPct={summary.profile.wantsPct}
          savingsPct={summary.profile.savingsPct}
          onClose={() => setGuideOpen(false)}
        />
      )}

      {editing && (
        <ProfileEditor
          identity={identity}
          month={month}
          monthName={monthLabel(month)}
          initial={summary.profile}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onProfileSaved();
          }}
        />
      )}
    </section>
  );
}

function ProfileEditor({
  identity,
  month,
  monthName,
  initial,
  onClose,
  onSaved,
}: {
  identity: Identity;
  month: string;
  monthName: string;
  initial: BudgetProfileInput;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [needs, setNeeds] = useState(String(initial.needsPct));
  const [wants, setWants] = useState(String(initial.wantsPct));
  const [savings, setSavings] = useState(String(initial.savingsPct));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsed = useMemo(
    () => ({ needsPct: Number(needs), wantsPct: Number(wants), savingsPct: Number(savings) }),
    [needs, wants, savings],
  );

  const total = parsed.needsPct + parsed.wantsPct + parsed.savingsPct;
  const valid =
    Number.isInteger(parsed.needsPct) &&
    Number.isInteger(parsed.wantsPct) &&
    Number.isInteger(parsed.savingsPct) &&
    total === 100;

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.setBudgetPeriod(identity, month, parsed);
      onSaved();
    } catch (e: unknown) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="profile-editor" onSubmit={save}>
      <p className="hint">
        Setting the split for <strong>{monthName}</strong> only. Other months are unaffected; a month
        you have not set up yet starts from the most recent one you have.
      </p>
      <div className="pct-row">
        <label>
          <span>Needs</span>
          <input inputMode="numeric" value={needs} onChange={(e) => setNeeds(e.target.value)} />
        </label>
        <label>
          <span>Wants</span>
          <input inputMode="numeric" value={wants} onChange={(e) => setWants(e.target.value)} />
        </label>
        <label>
          <span>Savings</span>
          <input inputMode="numeric" value={savings} onChange={(e) => setSavings(e.target.value)} />
        </label>
        <span className={valid ? 'remainder ok' : 'remainder off'}>
          {valid ? '100%' : `${Number.isFinite(total) ? total : 0}% — must be 100`}
        </span>
      </div>
      {error && <p className="error">{error}</p>}
      <div className="modal-actions">
        <div className="spacer" />
        <button type="button" className="chip" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="primary compact" disabled={!valid || busy}>
          {busy ? 'Saving…' : 'Save split'}
        </button>
      </div>
    </form>
  );
}
